import type {
  ExtendedDocument,
  ExtendedMediaTypeObject,
  ExtendedOperationObject,
  ExtendedReferenceObject,
  ExtendedResponseObject,
  ExtendedSchemaObject,
  JsonSchemaType,
  ResolvedExtendedSchemaObject,
  VisibleExcelProfile,
} from "@/services/open-api/openApi.types";

import { isRecord } from "@/utils/utils";

import { getSpeckleProfile } from "./equationExcelClientMetadata";

export type SchemaNode = ExtendedReferenceObject | ExtendedSchemaObject;

type SchemaPrimitiveType = Exclude<JsonSchemaType, unknown[]>;

const NULL_SCHEMA_TYPES = new Set<SchemaPrimitiveType>(["null"]);

const REFERENCE_ONLY_KEYS = new Set(["$ref", "summary"]);

export function isReferenceObject(schema: SchemaNode): schema is ExtendedReferenceObject {
  return "$ref" in schema && typeof schema.$ref === "string" && schema.$ref.trim() !== "";
}

function isJsonSchema(value: unknown): value is SchemaNode {
  return isRecord(value);
}

function toSchemaArray(value: SchemaNode[] | undefined): SchemaNode[] {
  return value ?? [];
}

function safeDecodePointerSegment(value: string): string {
  const unescaped = value.replace(/~1/g, "/").replace(/~0/g, "~");

  try {
    return decodeURIComponent(unescaped);
  } catch {
    return unescaped;
  }
}

function getRefSiblings(schema: ExtendedReferenceObject): Partial<ExtendedSchemaObject> {
  return Object.fromEntries(Object.entries(schema).filter(([key]) => !REFERENCE_ONLY_KEYS.has(key)));
}

function getSoleNonNullType(type: SchemaPrimitiveType[]): SchemaPrimitiveType | undefined {
  const nonNullTypes = type.filter((item) => !NULL_SCHEMA_TYPES.has(item));

  return nonNullTypes.length === 1 ? nonNullTypes[0] : undefined;
}

function getSchemaTypeValue(schema?: SchemaNode | null): SchemaPrimitiveType | undefined {
  if (!schema || isReferenceObject(schema)) {
    return undefined;
  }

  const type = schema.type;

  if (typeof type === "string") {
    return type;
  }

  if (Array.isArray(type)) {
    return getSoleNonNullType(type);
  }

  return undefined;
}

function isNullSchema(schema: SchemaNode): boolean {
  if (isReferenceObject(schema)) {
    return false;
  }

  const type = schema.type;

  if (typeof type === "string") {
    return NULL_SCHEMA_TYPES.has(type);
  }

  if (Array.isArray(type)) {
    return type.every((item) => NULL_SCHEMA_TYPES.has(item));
  }

  return false;
}

function firstResolvableSchema(
  apiSchema: ExtendedDocument,
  schemas: SchemaNode[]
): ResolvedExtendedSchemaObject | null {
  for (const schema of schemas) {
    const resolved = dereferenceSchema(apiSchema, schema);

    if (resolved) {
      return resolved;
    }
  }

  return null;
}

function normalizeEndpointPath(endpoint: string): string {
  let path = endpoint;

  try {
    path = new URL(endpoint).pathname;
  } catch {
    path = endpoint;
  }

  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  const withoutTrailingSlash = withLeadingSlash.replace(/\/+$/, "");

  return withoutTrailingSlash || "/";
}

function getPostOperation(apiSchema: ExtendedDocument, endpoint?: string | null): ExtendedOperationObject | null {
  if (!endpoint) {
    return null;
  }

  const targetPath = normalizeEndpointPath(endpoint);

  for (const [path, methods] of Object.entries(apiSchema.paths ?? {})) {
    if (normalizeEndpointPath(path) !== targetPath) {
      continue;
    }

    return methods?.post ?? null;
  }

  return null;
}

function getJsonContentSchema(content?: Record<string, ExtendedMediaTypeObject>): SchemaNode | null {
  const schema = content?.["application/json"]?.schema;

  return isJsonSchema(schema) ? schema : null;
}

function getResponseJsonContentSchema(response?: ExtendedReferenceObject | ExtendedResponseObject): SchemaNode | null {
  if (!response || "$ref" in response) {
    return null;
  }

  return getJsonContentSchema(response.content);
}

function getRequestBodyJsonSchema(operation: ExtendedOperationObject | null): SchemaNode | null {
  const requestBody = operation?.requestBody;

  if (!requestBody || "$ref" in requestBody) {
    return null;
  }

  return getJsonContentSchema(requestBody.content);
}

function getResponseJsonSchema(operation: ExtendedOperationObject | null): SchemaNode | null {
  if (!operation?.responses) {
    return null;
  }

  const response200Schema = getResponseJsonContentSchema(operation.responses["200"]);

  if (response200Schema) {
    return response200Schema;
  }

  const firstSuccessResponse = Object.entries(operation.responses).find(([status, response]) => {
    return /^2\d\d$/.test(status) && Boolean(getResponseJsonContentSchema(response));
  });

  return firstSuccessResponse ? getResponseJsonContentSchema(firstSuccessResponse[1]) : null;
}

function unwrapObjProperty(
  apiSchema: ExtendedDocument,
  schema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  const resolvedSchema = dereferenceSchema(apiSchema, schema);

  if (!resolvedSchema) {
    return null;
  }

  const objSchema = resolveObjectPropertySchema(apiSchema, "obj", resolvedSchema);

  return objSchema ?? resolvedSchema;
}

function getObjectChildValue(value: unknown, key: string): unknown {
  return isRecord(value) ? value[key] : undefined;
}

function getArrayItemValue(value: unknown, index: string): unknown {
  if (!Array.isArray(value) || !/^\d+$/.test(index)) {
    return undefined;
  }

  return value[Number(index)];
}

function getDirectPropertySchema(
  schema: ResolvedExtendedSchemaObject,
  key: string
): ResolvedExtendedSchemaObject | null {
  const property = schema.properties?.[key];

  if (property) {
    return property;
  }

  const additionalProperties = schema.additionalProperties;

  if (additionalProperties && typeof additionalProperties === "object") {
    return additionalProperties;
  }

  return null;
}

function resolveJsonPointer(apiSchema: ExtendedDocument, ref: string): SchemaNode | null {
  if (typeof ref !== "string" || !ref.startsWith("#/")) {
    return null;
  }

  const segments = ref.slice(2).split("/").map(safeDecodePointerSegment);
  let current: unknown = apiSchema;

  for (const segment of segments) {
    if (!isRecord(current) || !(segment in current)) {
      return null;
    }

    current = current[segment];
  }

  return isJsonSchema(current) ? current : null;
}

function dereferenceSchemaInternal(
  apiSchema: ExtendedDocument,
  seenRefs: Set<string>,
  schema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  if (!schema) {
    return null;
  }

  const nullable = resolveNullableSchema(schema);

  if (nullable && nullable !== schema) {
    return dereferenceSchemaInternal(apiSchema, seenRefs, nullable);
  }

  if (!isReferenceObject(schema)) {
    return { ...schema };
  }

  const ref = schema.$ref;

  if (seenRefs.has(ref)) {
    return null;
  }

  const referencedSchema = resolveJsonPointer(apiSchema, ref);

  if (!referencedSchema) {
    return null;
  }

  const nextSeenRefs = new Set(seenRefs);
  nextSeenRefs.add(ref);

  const resolvedReference = dereferenceSchemaInternal(apiSchema, nextSeenRefs, referencedSchema);

  if (!resolvedReference) {
    return null;
  }

  return {
    ...resolvedReference,
    ...getRefSiblings(schema),
  };
}

export function dereferenceSchema(
  apiSchema: ExtendedDocument,
  schema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  return dereferenceSchemaInternal(apiSchema, new Set(), schema);
}

export function resolveAnyOfBranches(apiSchema: ExtendedDocument, schema?: SchemaNode | null): SchemaNode[] | null {
  const seenRefs = new Set<string>();
  let currentSchema: SchemaNode | null | undefined = schema;

  while (currentSchema) {
    if (!isReferenceObject(currentSchema)) {
      const anyOf = toSchemaArray(currentSchema.anyOf);

      if (anyOf.length > 0) {
        const nonNullBranches = anyOf.filter((option) => !isNullSchema(option));

        if (nonNullBranches.length >= 2) {
          return nonNullBranches;
        }
      }

      return null;
    }

    const ref = currentSchema.$ref;

    if (seenRefs.has(ref)) {
      return null;
    }

    const referencedSchema = resolveJsonPointer(apiSchema, ref);

    if (!referencedSchema) {
      return null;
    }

    seenRefs.add(ref);
    currentSchema = referencedSchema;
  }

  return null;
}

export function resolveNullableSchema(
  schema?: ResolvedExtendedSchemaObject | null
): ResolvedExtendedSchemaObject | null;
export function resolveNullableSchema(schema?: SchemaNode | null): SchemaNode | null;
export function resolveNullableSchema(schema?: SchemaNode | null): SchemaNode | null {
  if (!schema) {
    return null;
  }

  if (isReferenceObject(schema)) {
    return schema;
  }

  const anyOf = toSchemaArray(schema.anyOf);

  if (anyOf.length > 0) {
    const nonNullSchema = anyOf.find((option) => !isNullSchema(option));

    return nonNullSchema ?? null;
  }

  const type = schema.type;

  if (Array.isArray(type)) {
    const soleNonNullType = getSoleNonNullType(type);

    if (soleNonNullType) {
      return {
        ...schema,
        type: soleNonNullType,
      };
    }
  }

  return schema;
}

function resolveArrayItemSchema(
  apiSchema: ExtendedDocument,
  rawSchema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  const schema = resolveSchemaForValue(apiSchema, rawSchema);

  if (!schema) {
    return null;
  }

  const items = schema.items;

  if (items) {
    return dereferenceSchema(apiSchema, items);
  }

  return null;
}

export function resolveObjectPropertySchema(
  apiSchema: ExtendedDocument,
  key: string,
  rawSchema?: SchemaNode | null
): ResolvedExtendedSchemaObject | null {
  const schema = resolveSchemaForValue(apiSchema, rawSchema);

  if (!schema) {
    return null;
  }

  const propertySchema = getDirectPropertySchema(schema, key);

  if (propertySchema) {
    return dereferenceSchema(apiSchema, propertySchema);
  }

  const oneOf = toSchemaArray(schema.oneOf);

  if (oneOf.length > 0) {
    return firstResolvableSchema(apiSchema, oneOf);
  }

  return null;
}

export function resolveSchemaForValue(
  apiSchema: ExtendedDocument,
  rawSchema?: SchemaNode | null,
  actualValue?: unknown
): ResolvedExtendedSchemaObject | null {
  const schema = dereferenceSchema(apiSchema, resolveNullableSchema(rawSchema));

  if (!schema) {
    return null;
  }

  const oneOf = toSchemaArray(schema.oneOf);

  if (oneOf.length === 0) {
    return schema;
  }

  const discriminator = schema.discriminator;
  const propertyName = discriminator?.propertyName;
  const discriminatorValue = propertyName ? getObjectChildValue(actualValue, propertyName) : undefined;

  if (propertyName && discriminatorValue !== undefined) {
    const discriminatorKey = String(discriminatorValue);
    const mappedRef = discriminator?.mapping?.[discriminatorKey];

    if (typeof mappedRef === "string") {
      const mappedSchema = dereferenceSchema(apiSchema, resolveJsonPointer(apiSchema, mappedRef));

      if (mappedSchema) {
        return mappedSchema;
      }
    }

    for (const option of oneOf) {
      const resolvedOption = dereferenceSchema(apiSchema, option);

      if (!resolvedOption) {
        continue;
      }

      const discriminatorPropertySchema = resolveObjectPropertySchema(apiSchema, propertyName, resolvedOption);

      if (!discriminatorPropertySchema) {
        continue;
      }

      if (discriminatorPropertySchema.const === discriminatorValue) {
        return resolvedOption;
      }

      const enumValues = discriminatorPropertySchema.enum ?? [];

      if (enumValues.some((value) => value === discriminatorValue)) {
        return resolvedOption;
      }
    }
  }

  if (propertyName) {
    return null;
  }

  return firstResolvableSchema(apiSchema, oneOf);
}

export function resolveWorkbookSchemaForProfile(
  apiSchema: ExtendedDocument,
  excelProfile: VisibleExcelProfile
): ResolvedExtendedSchemaObject | null {
  const speckleProfile = getSpeckleProfile(excelProfile.profile);
  const receiveOperation = getPostOperation(apiSchema, speckleProfile?.receive_speckle_endpoint);
  const receiveSchema = unwrapObjProperty(apiSchema, getResponseJsonSchema(receiveOperation));

  if (receiveSchema) {
    return receiveSchema;
  }

  const sendOperation = getPostOperation(apiSchema, speckleProfile?.send_speckle_endpoint);
  const sendSchema = unwrapObjProperty(apiSchema, getRequestBodyJsonSchema(sendOperation));

  if (sendSchema) {
    return sendSchema;
  }

  const coreEndpoints = excelProfile.profile.core_endpoints ?? [];

  if (coreEndpoints.length === 1) {
    return dereferenceSchema(apiSchema, getRequestBodyJsonSchema(getPostOperation(apiSchema, coreEndpoints[0])));
  }

  return null;
}

export function resolveSchemaAtPath(
  apiSchema: ExtendedDocument,
  rootSchema: SchemaNode,
  path: string,
  rootValue?: unknown
): ResolvedExtendedSchemaObject | null {
  let currentSchema = resolveSchemaForValue(apiSchema, rootSchema, rootValue);
  let currentValue = rootValue;
  const parts = path.split(".").filter(Boolean);

  if (parts.length === 0) {
    return currentSchema;
  }

  for (const part of parts) {
    currentSchema = resolveSchemaForValue(apiSchema, currentSchema, currentValue);

    if (!currentSchema) {
      return null;
    }

    const propertySchema = resolveObjectPropertySchema(apiSchema, part, currentSchema);

    if (propertySchema) {
      currentSchema = propertySchema;
      currentValue = getObjectChildValue(currentValue, part);
      continue;
    }

    const itemSchema = resolveArrayItemSchema(apiSchema, currentSchema);

    if (itemSchema && /^\d+$/.test(part)) {
      const itemValue = getArrayItemValue(currentValue, part);
      currentSchema = resolveSchemaForValue(apiSchema, itemSchema, itemValue);
      currentValue = itemValue;
      continue;
    }

    return null;
  }

  return resolveSchemaForValue(apiSchema, currentSchema, currentValue);
}

export function getSchemaUnitLabel(schema?: ResolvedExtendedSchemaObject | null): string {
  const unit = schema?.unit;

  if (!unit) {
    return "";
  }

  const desiredFormatted = unit.formatted?.metric;

  if (desiredFormatted?.pretty_print) {
    return desiredFormatted.pretty_print;
  }

  const desiredUnit = unit.metric;

  if (typeof desiredUnit === "string") {
    return desiredUnit;
  }

  const baseFormatted = unit.formatted?.base;

  if (baseFormatted?.pretty_print) {
    return baseFormatted.pretty_print;
  }

  return unit.base ?? "";
}

export function getSchemaDescription(schema?: ResolvedExtendedSchemaObject | null): string | undefined {
  return typeof schema?.description === "string" ? schema.description : undefined;
}

export function getSchemaTitle(schema?: ResolvedExtendedSchemaObject | null): string | undefined {
  const title = typeof schema?.title === "string" ? schema.title.trim() : "";

  return title === "" ? undefined : title;
}

export function getSchemaEnumValues(apiSchema: ExtendedDocument, rawSchema?: SchemaNode | null): string[] {
  const schema = dereferenceSchema(apiSchema, rawSchema);

  if (!schema) {
    return [];
  }

  const nullable = resolveNullableSchema(schema);
  const enumValues = nullable?.enum ?? [];

  return enumValues.filter((value): value is string => typeof value === "string");
}

export function getSchemaPrimitiveType(
  apiSchema: ExtendedDocument,
  rawSchema?: SchemaNode | null
): SchemaPrimitiveType | undefined {
  const schema = dereferenceSchema(apiSchema, rawSchema);
  const nullable = resolveNullableSchema(schema);

  return getSchemaTypeValue(nullable);
}
